import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import KPICard from '../Dashboard/KPICard';
import TimelineSwitcher from '../Common/TimelineSwitcher';

const { FiTarget, FiTrendingUp, FiDollarSign, FiPackage } = FiIcons;

const SalesVolumePerformance = ({ productId }) => {
  const [timeline, setTimeline] = useState('YTD');

  // Mock KPI data
  const kpiSections = [
    {
      title: 'Target/Budget',
      kpis: [
        { title: 'Target Market Share', value: '45%', change: '+2.5%', changeType: 'positive', icon: FiTarget, color: 'blue' },
        { title: 'Target Budget', value: '$52.3M', change: '+8.1%', changeType: 'positive', icon: FiDollarSign, color: 'green' },
        { title: 'Budgeted Volume', value: '2.8M', change: '+5.2%', changeType: 'positive', icon: FiPackage, color: 'purple' }
      ]
    },
    {
      title: 'Novadoz Sales Register',
      kpis: [
        { title: 'Total Sales (YTD)', value: '$45.2M', change: '+12.5%', changeType: 'positive', icon: FiDollarSign, color: 'blue' },
        { title: 'Total Sales (MAT)', value: '$58.7M', change: '+8.3%', changeType: 'positive', icon: FiDollarSign, color: 'blue' },
        { title: 'Total Volume (YTD)', value: '2.85M', change: '+15.7%', changeType: 'positive', icon: FiPackage, color: 'green' }
      ]
    },
    {
      title: 'IQVIA Market Data',
      kpis: [
        { title: 'Market Sales (YTD)', value: '$187.3M', change: '+6.2%', changeType: 'positive', icon: FiTrendingUp, color: 'orange' },
        { title: 'Market Volume (YTD)', value: '12.4M', change: '+4.8%', changeType: 'positive', icon: FiPackage, color: 'orange' },
        { title: 'Our Market Share', value: '24.1%', change: '+3.2%', changeType: 'positive', icon: FiTarget, color: 'purple' }
      ]
    }
  ];

  // Mock sales table data
  const salesTableData = [
    {
      strength: '250mg',
      sku: '72578-001-30',
      sales2022: 18500000,
      sales2023: 22800000,
      sales2024: 28600000,
      volume2022: 1200000,
      volume2023: 1485000,
      volume2024: 1850000,
      eaches2022: 2400000,
      eaches2023: 2970000,
      eaches2024: 3700000
    },
    {
      strength: '500mg',
      sku: '72578-001-60',
      sales2022: 12300000,
      sales2023: 15600000,
      sales2024: 16600000,
      volume2022: 580000,
      volume2023: 735000,
      volume2024: 780000,
      eaches2022: 1160000,
      eaches2023: 1470000,
      eaches2024: 1560000
    }
  ];

  // Mock competitor data
  const competitorData = [
    { company: 'Novadoz', volume: 2630000, marketShare: 24.1, color: '#3b82f6' },
    { company: 'Biocon', volume: 1850000, marketShare: 16.9, color: '#10b981' },
    { company: 'Dr Reddys', volume: 1620000, marketShare: 14.8, color: '#f59e0b' },
    { company: 'Aurobindo', volume: 1380000, marketShare: 12.6, color: '#8b5cf6' },
    { company: 'Cipla', volume: 1150000, marketShare: 10.5, color: '#ef4444' },
    { company: 'Sun Pharma', volume: 980000, marketShare: 9.0, color: '#06b6d4' },
    { company: 'Lupin', volume: 850000, marketShare: 7.8, color: '#84cc16' },
    { company: 'Others', volume: 440000, marketShare: 4.3, color: '#6b7280' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="space-y-6">
      {/* KPI Sections */}
      {kpiSections.map((section, sectionIndex) => (
        <motion.div
          key={section.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: sectionIndex * 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {section.kpis.map((kpi, index) => (
              <KPICard key={index} {...kpi} delay={0} />
            ))}
          </div>
        </motion.div>
      ))}

      {/* Sales Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Historical Sales Performance</h3>
            <TimelineSwitcher
              value={timeline}
              onChange={setTimeline}
              options={['Last Years Sales', 'MAT', 'YTD', 'Last 30 Days', 'Current Month']}
            />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Strength</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">SKU</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900" colSpan="3">Sales (USD)</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900" colSpan="3">Volume (Packs)</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900" colSpan="3">Volume (Eaches)</th>
              </tr>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th></th>
                <th></th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2022</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2023</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2024</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2022</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2023</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2024</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2022</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2023</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2024</th>
              </tr>
            </thead>
            <tbody>
              {salesTableData.map((row, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-6 font-medium text-gray-900">{row.strength}</td>
                  <td className="py-4 px-6">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {row.sku}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-center text-sm">{formatCurrency(row.sales2022)}</td>
                  <td className="py-4 px-6 text-center text-sm">{formatCurrency(row.sales2023)}</td>
                  <td className="py-4 px-6 text-center text-sm font-medium">{formatCurrency(row.sales2024)}</td>
                  <td className="py-4 px-6 text-center text-sm">{formatNumber(row.volume2022)}</td>
                  <td className="py-4 px-6 text-center text-sm">{formatNumber(row.volume2023)}</td>
                  <td className="py-4 px-6 text-center text-sm font-medium">{formatNumber(row.volume2024)}</td>
                  <td className="py-4 px-6 text-center text-sm">{formatNumber(row.eaches2022)}</td>
                  <td className="py-4 px-6 text-center text-sm">{formatNumber(row.eaches2023)}</td>
                  <td className="py-4 px-6 text-center text-sm font-medium">{formatNumber(row.eaches2024)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Competitor Market Share */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Market Share Analysis (Last 12 Months)</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Market Share Chart */}
          <div>
            <div className="relative h-64 flex items-center justify-center">
              <div className="relative w-48 h-48">
                {/* Simple pie chart representation */}
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                  {competitorData.reduce((acc, company, index) => {
                    const startAngle = acc.currentAngle;
                    const endAngle = startAngle + (company.marketShare / 100) * 360;
                    const largeArcFlag = company.marketShare > 50 ? 1 : 0;
                    
                    const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                    const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                    const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                    const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                    
                    const pathData = [
                      `M 50 50`,
                      `L ${x1} ${y1}`,
                      `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                      `Z`
                    ].join(' ');
                    
                    acc.paths.push(
                      <path
                        key={index}
                        d={pathData}
                        fill={company.color}
                        className="hover:opacity-80 transition-opacity"
                      />
                    );
                    
                    acc.currentAngle = endAngle;
                    return acc;
                  }, { paths: [], currentAngle: 0 }).paths}
                </svg>
                
                {/* Center label */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">24.1%</p>
                    <p className="text-sm text-gray-600">Our Share</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Legend and Data */}
          <div className="space-y-3">
            {competitorData.map((company, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: company.color }}
                  ></div>
                  <span className="font-medium text-gray-900">{company.company}</span>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{company.marketShare}%</p>
                  <p className="text-sm text-gray-600">{formatNumber(company.volume)} units</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SalesVolumePerformance;